import { z } from 'zod/v4';
import { FindingResourceSchema } from './finding-resource.js';

export const FindingDetailParamsSchema = z.object({
  id: z.string().min(1, 'Finding ID is required'),
});

export type FindingDetailParams = z.infer<typeof FindingDetailParamsSchema>;

/**
 * One audit finding as `GET /api/capa/findings/details/:id` reports it.
 *
 * The list resource plus the three fields only the detail read produces, minus
 * the two that are list-only. Where it deliberately differs from a list row:
 *
 * - **`isArchived` is a boolean here**, while every other CAPA resource reports
 *   the raw 0/1 (`ActionResource.isArchived`, `PlanFinding.isArchived`). That is
 *   parity with the endpoint as shipped, not a preference: both dashboards test
 *   it truthily, so flipping it now would be a silent no-op on one and a wire
 *   break on the other. It is per-company — the reading company's own archive
 *   shelf, not a column on the finding.
 * - **`rootCauseAnalysis` and `genderedImplications`** are the DELTA-2311
 *   restricted fields. They are masked to `null` in SQL for any company that is
 *   neither the finding's creator nor the company it is about, so the presence of
 *   the keys says nothing about whether the caller may see the values.
 * - **`actionCount` and `planCount` count exactly as they do on a list row** —
 *   the detail read carries the list's subqueries verbatim. It used to count its
 *   own way, unscoped and with `planCount` reading `CAP`-type rows by
 *   `audit_finding_id`, a column no writer sets on a plan; that made the
 *   detail's `planCount` permanently `0` while the list, the Associated-plans
 *   tab and the export all reported the plans reached through child actions.
 * - **`companyName`** is populated. The list had it and the detail did not, which
 *   left a dead fallback in frontend-v2's `useResponsibleCompany`; the module
 *   read adds the same `company_profiles` join, so the fallback now resolves.
 * - `isLinkedToPlan` and `createdByUserName` are absent — neither is selected by
 *   the detail SQL.
 *
 * `dueDate`, `createdAt` and `updatedAt` are `Date`s here as they are on a list
 * row, though the route this replaced hand-stringified them. Both forms
 * serialise to the same ISO string, so the wire is unchanged.
 */
export const FindingDetailSchema = FindingResourceSchema.omit({
  isLinkedToPlan: true,
  createdByUserName: true,
}).extend({
  companyName: z.string().nullable(),
  isArchived: z.boolean(),
  rootCauseAnalysis: z.string().nullable(),
  genderedImplications: z.string().nullable(),
});

export type FindingDetail = z.infer<typeof FindingDetailSchema>;
